﻿import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setHoveredWorkflow,
  selectHoveredWorkflow,
} from "@/features/homepage/homeSlice";
import { setSelectedWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import "./Arrow.css";

export const wfStates = {
  default: "default",
  hovered: "hovered",
  selected: "selected",
  popupSelected: "popupSelected",
  lowlighted: "lowlighted",
  disabled: "disabled",
  colored: "colored",
};
const halfArrowWidth = 11;
const halfArrowHeight = 8;
const arrowOffset = 5;

/*
The specific offset we require depends on the relative position of the arrow and its artifact,
this function only fully describes the artifact map. Not the full universe of possible connection
configurations. It may need additional logic if the map is expanded in the future.
*/
const arrowOffsetHelper = (
  startX,
  startY,
  endX,
  endY,
  turns,
  direction,
  angle
) => {
  if (direction === "horizontal") {
    if (turns === 2 || turns === 0) {
      if (startX >= endX) {
        return `translate(${endX - halfArrowWidth - arrowOffset}, ${
          endY - halfArrowHeight
        }) rotate(${angle}, ${halfArrowWidth}, ${halfArrowHeight})`;
      } else {
        return `translate(${endX - halfArrowWidth + arrowOffset}, ${
          endY - halfArrowHeight
        }) rotate(${angle}, ${halfArrowWidth}, ${halfArrowHeight})`;
      }
    } else {
      if (startX >= endX) {
        return `translate(${endX - halfArrowWidth - arrowOffset}, ${
          endY - halfArrowHeight
        }) rotate(${angle}, ${halfArrowWidth}, ${halfArrowHeight})`;
      } else {
        return `translate(${endX - halfArrowWidth}, ${
          endY - halfArrowHeight + arrowOffset
        }) rotate(${angle}, ${halfArrowWidth}, ${halfArrowHeight})`;
      }
    }
  } else {
    if (startX > endX) {
      return `translate(${endX - (2 * halfArrowWidth) + arrowOffset}, ${
        endY - halfArrowHeight
      }) rotate(${angle}, ${halfArrowWidth}, ${halfArrowHeight})`;
    } else {
      return `translate(${endX - halfArrowWidth}, ${
        endY - halfArrowHeight + arrowOffset
      }) rotate(${angle}, ${halfArrowWidth}, ${halfArrowHeight})`;
    }
  }
};

const Arrow = ({
  operationId,
  source,
  target,
  startX,
  startY,
  endX,
  endY,
  label,
  turns = 0,
  notTrueWorkflow,
  highlighted,
  workflowState,
  setWorkflowState,
  workflowSummaryStatus,
  handleWorkflow,
  direction,
}) => {
  const dispatch = useDispatch();
  const hoveredWorkflow = useSelector(selectHoveredWorkflow);
  const curveRadius = 15; // Standardized curve radius
  const [disabled, setDisabled] = useState(false);
  let pathData;
  let finalSegmentStartX, finalSegmentStartY;

  if (turns === 0) {
    // Straight arrow
    pathData = `M ${startX} ${startY} L ${endX} ${endY}`;
    finalSegmentStartX = startX;
    finalSegmentStartY = startY;
  } else if (turns === 1) {
    // One 90-degree turn
    if (endX > startX) {
      // Turn to the right
      pathData = `
                M ${startX} ${startY}
                H ${endX - curveRadius}
                Q ${endX} ${startY}, ${endX} ${startY + curveRadius}
                V ${endY}
            `;
      finalSegmentStartX = endX;
      finalSegmentStartY = startY + curveRadius;
    } else {
      // Turn to the left, but go down first
      pathData = `
                M ${startX} ${startY}
                V ${endY - curveRadius}
                Q ${startX} ${endY}, ${startX - curveRadius} ${endY}
                H ${endX}
            `;
      finalSegmentStartX = startX - curveRadius;
      finalSegmentStartY = endY;
    }
  } else if (turns === 2) {
    // Two 90-degree turns
    const midX = (startX + endX) / 1.465;
    if (endY < startY) {
      // First turn clockwise, then counterclockwise
      pathData = `
                M ${startX} ${startY}
                H ${midX + curveRadius}
                Q ${midX} ${startY}, ${midX} ${startY - curveRadius}
                V ${endY + curveRadius}
                Q ${midX} ${endY}, ${midX - curveRadius} ${endY}
                H ${endX}
            `;
      finalSegmentStartX = midX - curveRadius;
      finalSegmentStartY = endY;
    } else {
      // First turn counterclockwise, then clockwise
      pathData = `
                M ${startX} ${startY}
                H ${midX + curveRadius}
                Q ${midX} ${startY}, ${midX} ${startY + curveRadius}
                V ${endY - curveRadius}
                Q ${midX} ${endY}, ${midX - curveRadius} ${endY}
                H ${endX}
            `;
      finalSegmentStartX = midX - curveRadius;
      finalSegmentStartY = endY;
    }
  }

  useEffect(() => {
    setDisabled(
      workflowState === wfStates.disabled || (notTrueWorkflow ?? false)
    );
  }, [workflowState, notTrueWorkflow]);

  const handleClick = () => {
    if (workflowState === wfStates.selected) {
      dispatch(setSelectedWorkflow({ selectedWorkflow: 0 }));
      setWorkflowState(wfStates.default);
    } else if (
      workflowState !== wfStates.popupSelected &&
      workflowState !== wfStates.selected
    ) {
      dispatch(setSelectedWorkflow({ selectedWorkflow: operationId }));

      // heap.io
      window.heap.track("Workflow Map - Select Workflow");
    }
  };
  const handleMouseEnter = () => {
    dispatch(setHoveredWorkflow({ hoveredWorkflow: operationId }));
    setWorkflowState(wfStates.hovered);
  };

  const handleMouseLeave = () => {
    dispatch(setHoveredWorkflow({ hoveredWorkflow: 0 }));
    setWorkflowState(wfStates.default);
  };

  const getStrokeColor = () => {
    switch (workflowState) {
      case wfStates.lowlighted:
      case wfStates.disabled:
        return "var(--grey-lowlight)";
      default:
        return "var(--primary-main)";
    }
  };

  const getFilter = () => {
    if (notTrueWorkflow) {
      return "none";
    }
    if (
      workflowState === wfStates.hovered ||
      workflowState === wfStates.selected
    ) {
      return "";
    }
    return "none";
  };

  // Calculate the angle of the arrowhead based on the final segment
  const deltaX = endX - finalSegmentStartX;
  const deltaY = endY - finalSegmentStartY;
  const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) - 90;

  const pathClassName = `path-${workflowState} ${
    operationId === hoveredWorkflow ? "workflow-hovered" : ""
  }`;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: "absolute", top: 0, left: 0 }}
      >
        <path
          d={pathData}
          stroke={getStrokeColor()}
          fill="none"
          strokeDasharray={notTrueWorkflow ? "10" : "none"}
          strokeLinecap="round"
          className={`path-${workflowState} arrow-path ${
            operationId === hoveredWorkflow ? "workflow-hovered" : ""
          }`}
          style={{
            cursor: "pointer",
            pointerEvents: disabled ? "none" : "stroke",
            transition: "stroke 0.3s, stroke-width 0.3s",
            filter: getFilter(),
          }}
          onMouseEnter={!notTrueWorkflow ? handleMouseEnter : undefined}
          onMouseLeave={!notTrueWorkflow ? handleMouseLeave : undefined}
        />
        <path
          d={pathData}
          stroke="transparent"
          strokeWidth="15"
          fill="none"
          onClick={!notTrueWorkflow ? handleClick : undefined}
          onMouseEnter={!notTrueWorkflow ? handleMouseEnter : undefined}
          onMouseLeave={!notTrueWorkflow ? handleMouseLeave : undefined}
          zindex="2"
          className={pathClassName}
          style={{
            cursor: "pointer",
            pointerEvents: disabled ? "none" : "stroke",
          }}
        />
        <path
          d="M11.7347 15.1744L20.9377 1.62435C21.3887 0.960405 20.9131 0.0625 20.1105 0.0625L1.70443 0.0625C0.90183 0.0625 0.426252 0.960405 0.877195 1.62435L10.0802 15.1744C10.477 15.7586 11.3379 15.7586 11.7347 15.1744Z"
          fill={getStrokeColor()}
          transform={arrowOffsetHelper(
            startX,
            startY,
            endX,
            endY,
            turns,
            direction,
            angle
          )}
          filter={getFilter()}
          className={`path-${workflowState} arrow-head ${
            operationId === hoveredWorkflow ? "workflow-hovered" : ""
          }`}
        />
      </svg>
    </Box>
  );
};

export default Arrow;
